import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Inbox, Plus, RefreshCw, Settings } from 'lucide-react';
import Toast from '../components/Toast';
import QueueOverlay from '../components/QueueOverlay';
import LuckyPayModal from '../components/LuckyPayModal';
import OrdersScreenSkeleton from '../components/OrdersScreenSkeleton';
import TaskDetailSheet from '../components/TaskDetailSheet';
import taskService from '../services/taskService';
import API_CONFIG from '../config/api';
import { formatMoney, getActionMeta, getPlatformMeta } from '../data/taskMeta';
import { useTranslation } from '../i18n/LocaleContext';

// 这一屏按设计稿 1:1 复刻：浅灰底 + 大圆角白卡 + 超大标题 + 黑色胶囊主按钮。
// 卡片顺序 = 当前订单状态卡（带四段流程条）→ 幸运订单卡 → 今日订单卡 → 底部随机待接订单卡集。
// 价格、当天单数、幸运订单全部走新手剧本同一套口径。
const CLAIMED = 'claimed';
const FONT_STACK = 'system-ui, -apple-system, "SF Pro Display", "SF Pro Text", "Segoe UI", sans-serif';

// 底部卡集的背景色，按下标取模，保证相邻卡片不同色。
const OFFER_COLORS = ['#B4794F', '#2F4A3E', '#3B4A6B', '#7A4B57', '#6B5B3E', '#4A4550', '#8C5A3C', '#37504F'];

// 派单平台白名单：勾选哪些平台，随机派单与底部卡集就只出这些平台的单，
// 避免客户被派到未绑定平台的单上被 SOCIAL_REQUIRED 拦下。
const ORDER_PLATFORMS = ['tiktok', 'facebook', 'instagram', 'youtube'];
const PLATFORM_STORE_KEY = 'orderPlatforms';

const OrdersPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [today, setToday] = useState(null);
  const [feed, setFeed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [luckyPayId, setLuckyPayId] = useState(null);
  const [claiming, setClaiming] = useState(false);
  const [queuing, setQueuing] = useState(false);
  const [activeTask, setActiveTask] = useState(null);
  const [platforms, setPlatforms] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(PLATFORM_STORE_KEY));
      if (Array.isArray(saved)) return saved;
    } catch (e) { /* 首次或数据损坏时回退全选 */ }
    return ORDER_PLATFORMS;
  });
  const togglePlatform = useCallback((p) => {
    setPlatforms((prev) => {
      const next = prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p];
      try { localStorage.setItem(PLATFORM_STORE_KEY, JSON.stringify(next)); } catch (e) { /* 忽略写入失败 */ }
      return next;
    });
  }, []);

  const load = useCallback(async () => {
    try {
      const [todayRes, feedRes] = await Promise.all([taskService.getToday(), taskService.getFeed()]);
      setToday(todayRes);
      setFeed(feedRes.tasks || []);
    } catch (error) {
      setToast({ type: 'error', message: error?.response?.data?.error || t('ordLoading') });
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const stats = today?.stats;
  const limit = Math.max(1, Number(today?.dailyLimit || 1));
  const done = Number(stats?.total || 0);
  const pct = Math.min(100, Math.round((done / limit) * 100));
  const pendingLucky = useMemo(() => today?.pendingLuckyOrders || [], [today]);
  // 已支付/已退款的幸运订单混入今日订单列表（待审核一直显示，已到账/已退款仅当天）
  const luckyRows = useMemo(() => today?.luckyOrders || [], [today]);

  // 有待审核任务时轮询刷新：自动过审改到提交后随机 40–60s，靠这里把结果刷进界面，无需手动刷新。
  useEffect(() => {
    if (!Number(stats?.pendingReview || 0)) return undefined;
    const timer = setInterval(() => { load(); }, 5000);
    return () => clearInterval(timer);
  }, [stats?.pendingReview, load]);

  const resumeTask = useMemo(
    () => feed.find((item) => item.openTask && item.openTask.status === CLAIMED) || null,
    [feed]
  );
  const nextTask = useMemo(
    () => feed.find((item) => item.available && !item.openTask && platforms.includes(item.platform)) || null,
    [feed, platforms]
  );
  const featured = resumeTask || nextTask;
  // 底部卡集 = 待接订单集合，每次进页面随机排一次。
  // 种子只播种一次，避免 re-render 时卡片乱跳。
  const [offerSeed] = useState(() => Math.random());
  const offerTasks = useMemo(() => {
    // 顶部大图已经在展示当前这一单，底部卡集优先避开它，避免同图同屏出现两次。
    const all = feed.filter((item) => item.available && !item.openTask && platforms.includes(item.platform));
    const pool = all.filter((item) => item.id !== featured?.id);
    const keyed = pool.map((item) => ({
      item,
      k: Math.abs(Math.sin((item.id || 1) * 12.9898 + offerSeed * 78.233) * 43758.5453) % 1,
    }));
    return keyed.sort((a, b) => a.k - b.k).map((x) => x.item).slice(0, 8);
  }, [feed, featured, offerSeed, platforms]);

  const rows = useMemo(() => {
    const list = [...(today?.tasks || [])].sort((a, b) => Number(a.id) - Number(b.id));
    return list.map((row, index) => ({ ...row, position: index + 1 }));
  }, [today]);

  // 置顶卡对应的是已领取那一单本身，编号和金额都取真实值，
  // 不能用「下一档」的序号和价格，否则同一单会出现两个号、两个价。
  const resumeRow = useMemo(() => {
    if (!resumeTask) return null;
    return rows.find((row) => Number(row.task_id) === Number(resumeTask.id)) || null;
  }, [rows, resumeTask]);

  // 流程条：已领取 → 已提交 → 已审核 → 已到账
  const stage = resumeTask ? 1 : Number(stats?.pendingReview || 0) > 0 ? 2 : Number(stats?.approved || 0) > 0 ? 3 : 0;
  const heroTitle = resumeTask
    ? t('ordHeroClaimed')
    : stage === 2
      ? t('ordHeroReview')
      : done >= limit
        ? t('ordHeroPaid')
        : t('ordHeroIdle');
  const heroSubtitle = featured
    ? `${featured.title}${resumeRow ? ` · ${t('orderPrefix')} ${resumeRow.position}` : ` · ${t('orderPrefix')} ${done + 1}`}`
    : `${done}/${limit}`;

  const payLucky = useCallback((order) => {
    setLuckyPayId(order.id);
  }, []);

  const handleLuckyPaid = useCallback(async () => {
    setLuckyPayId(null);
    setToast({ type: 'success', message: t('luckyPaid') });
    await load();
  }, [load, t]);

  // 主按钮 = 领取下一单，领完直接进做单页传凭证
  const placeOrder = useCallback(async (task) => {
    setClaiming(true);
    setQueuing(true);
    // The 排单 overlay holds the screen for a random 7–30s beat.
    const holdMs = 7000 + Math.floor(Math.random() * 23001);
    try {
      await Promise.all([
        taskService.claim(task.id),
        new Promise((resolve) => setTimeout(resolve, holdMs)),
      ]);
      setToast({ type: 'success', message: t('ordClaimOk') });
      const fresh = await taskService.getFeed();
      setFeed(fresh.tasks || []);
      const opened = (fresh.tasks || []).find((item) => item.id === task.id);
      if (opened) setActiveTask(opened);
      await load();
    } catch (error) {
      if (error?.errorCode === 'KYC_REQUIRED') {
        setToast({ type: 'error', message: t('ordKycRequired') });
      } else if (error?.errorCode === 'SOCIAL_REQUIRED') {
        // 未绑定该平台账号：带去钱包页自动打开绑定弹层
        setToast({ type: 'error', message: t('socialRequiredToOrder').replace('{platform}', getPlatformMeta(error.platform).label) });
        navigate('/vi?social=1');
      } else if (error?.errorCode === 'SOCIAL_PENDING') {
        setToast({ type: 'error', message: t('socialPendingToOrder').replace('{platform}', getPlatformMeta(error.platform).label) });
      } else {
        setToast({ type: 'error', message: error?.message || t('ordClaimFailed') });
      }
    } finally {
      setClaiming(false);
      setQueuing(false);
    }
  }, [load, navigate, t]);

  const blockedByLucky = pendingLucky.length > 0;
  const primaryLabel = blockedByLucky
    ? t('ordLuckyFirst')
    : !featured
      ? t('ordNoTask')
      : resumeTask
        ? t('ordContinueTask')
        : t('ordPlace');

  const handlePrimary = () => {
    if (!featured || blockedByLucky) return;
    if (resumeTask) {
      setActiveTask(resumeTask);
      return;
    }
    placeOrder(nextTask);
  };

  if (loading) {
    return <OrdersScreenSkeleton />;
  }

  const heroImage = API_CONFIG.getImageUrl(featured?.imageUrl) || '/images/placeholder.svg';

  return (
    <div className="min-h-screen bg-canvas pt-5 pb-28" style={{ fontFamily: FONT_STACK }}>
      {toast && <Toast type={toast.type} message={toast.message} onClose={() => setToast(null)} />}
      {queuing && <QueueOverlay />}

      {/* 当前订单状态 + 四段流程条 */}
      <div className="max-w-md mx-auto px-4 mt-4">
        <div className="rounded-xl bg-white border border-black/[0.05] p-4">
          <div className="relative pr-[76px]">
            <p className="text-[15px] font-bold leading-[1.35] text-[#1D1D1F]">{heroTitle}</p>
            <p className="mt-1.5 text-[13px] leading-snug text-[#8E8E93] line-clamp-2 break-words">{heroSubtitle}</p>
            <img
              src={heroImage}
              alt=""
              className="absolute right-0 top-0 w-[64px] h-[64px] rounded-xl object-cover bg-canvas"
              onError={(e) => { e.target.src = '/images/placeholder.svg'; }}
            />
          </div>

          <div
            className="mt-4 h-2 rounded-full bg-[#E9E9EB] overflow-hidden"
            role="progressbar"
            aria-valuenow={done}
            aria-valuemin={0}
            aria-valuemax={limit}
          >
            <div className="op-fill" style={{ width: `${Math.max(pct, 8)}%` }} />
          </div>
          <div className="mt-2.5 flex items-center justify-between">
            {[
              { key: 'flowOne', at: 1 },
              { key: 'flowTwo', at: 2 },
              { key: 'flowThree', at: 3 },
              { key: 'flowFour', at: 4 }
            ].map((step) => (
              <span
                key={step.key}
                className={`text-[11px] ${stage >= step.at ? 'text-[#1D1D1F]' : 'text-[#B9B9BE]'}`}
              >
                {t(step.key)}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* 幸运订单未支付时挡住后续做单 */}
      {pendingLucky.length > 0 && (
        <div className="max-w-md mx-auto px-4 mt-4 space-y-3">
          {pendingLucky.map((order) => {
            const platform = getPlatformMeta(order.display_platform);
            const action = order.display_action ? getActionMeta(order.display_action) : null;
            return (
            <div key={order.id} className="rounded-xl bg-[#1D1D1F] p-4 text-white">
              <div className="flex items-start gap-3">
                <img
                  src={API_CONFIG.getImageUrl(order.display_image) || '/images/placeholder.svg'}
                  alt={order.display_title || ''}
                  className="w-11 h-11 shrink-0 rounded-xl bg-white/10 object-cover"
                  loading="lazy"
                  onError={(e) => { e.target.src = '/images/placeholder.svg'; }}
                />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[14px] font-semibold text-white">{order.display_title || t('ordFeatured')}</p>
                  <p className="mt-0.5 truncate text-[12px] text-white/50">
                    {t('orderPrefix')} {order.position} · {platform.label}
                    {action ? ` · ${t(action.verbKey)}` : ''}
                  </p>
                </div>
                <p className="shrink-0 tabular text-[19px] font-bold leading-none">{formatMoney(order.amount)}</p>
              </div>
              <p className="mt-3 text-[13px] leading-snug text-white/75">{t('luckyDesc', { payout: formatMoney(order.payout) })}</p>
              <button
                type="button"
                onClick={() => payLucky(order)}
                className="mt-3 w-full h-[42px] rounded-lg bg-white text-[#1D1D1F] text-[14px] font-bold disabled:opacity-60"
              >
                {`${t('luckyPay')} · ${formatMoney(order.payout)}`}
              </button>
            </div>
            );
          })}
        </div>
      )}

      {/* 今日订单 */}
      <div className="max-w-md mx-auto px-4 mt-6">
        <div className="rounded-xl bg-white border border-black/[0.05] p-4">
          <h2 className="text-[18px] font-bold leading-tight text-[#1D1D1F]">{t('ordTitle')}</h2>

          {rows.length > 0 || luckyRows.length > 0 ? (
            <div className="mt-4 divide-y divide-[#F0F0F2]">
              {luckyRows.map((order) => {
                const platform = getPlatformMeta(order.display_platform);
                return (
                  <div key={`lucky-${order.id}`} className="flex items-center gap-3 py-3">
                    <img
                      src={API_CONFIG.getImageUrl(order.display_image) || '/images/placeholder.svg'}
                      alt={order.display_title || ''}
                      className="w-11 h-11 shrink-0 rounded-xl bg-canvas object-cover"
                      loading="lazy"
                      onError={(e) => { e.target.src = '/images/placeholder.svg'; }}
                    />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-[14px] font-medium text-[#1D1D1F]">{order.display_title || t('luckyBadge')}</p>
                      <p className="mt-0.5 truncate text-[12px] text-[#8E8E93]">
                        {t('orderPrefix')} {order.position} · {platform.label}
                      </p>
                    </div>
                    <span className={`shrink-0 tabular text-[14px] font-bold ${order.status === 'paid' ? 'text-[#5B3FD6]' : 'text-[#8E8E93]'}`}>
                      {formatMoney(Number(order.status === 'paid' ? order.payout : order.amount))}
                    </span>
                  </div>
                );
              })}
              {rows.map((row) => {
                const platform = getPlatformMeta(row.platform);
                return (
                  <div key={row.id} className="flex items-center gap-3 py-3">
                    <img
                      src={API_CONFIG.getImageUrl(row.image_url) || '/images/placeholder.svg'}
                      alt={row.title}
                      className="w-11 h-11 shrink-0 rounded-xl bg-canvas object-cover"
                      loading="lazy"
                      onError={(e) => { e.target.src = '/images/placeholder.svg'; }}
                    />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-[14px] font-medium text-[#1D1D1F]">{row.title}</p>
                      <p className="mt-0.5 truncate text-[12px] text-[#8E8E93]">
                        {t('orderPrefix')} {row.position} · {platform.label}
                      </p>
                    </div>
                    <span className={`shrink-0 tabular text-[14px] font-bold ${row.status === 'approved' ? 'text-[#5B3FD6]' : 'text-[#8E8E93]'}`}>
                      {formatMoney(Number(row.reward))}
                    </span>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="mt-4 py-6 text-center">
              <Inbox className="w-6 h-6 text-[#B9B9BE] mx-auto" />
              <p className="mt-2 text-[13px] text-[#8E8E93]">{t('ordEmpty')}</p>
            </div>
          )}

          <div className="mt-4 grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => resumeTask && setActiveTask(resumeTask)}
              disabled={!resumeTask}
              className="h-[42px] rounded-lg bg-[#F0F0F2] flex items-center justify-center gap-2 px-2 text-[14px] font-semibold text-[#9B9BA1] whitespace-nowrap disabled:cursor-not-allowed disabled:opacity-50"
              title={t('taskSubmitProof')}
            >
              <RefreshCw className="w-[17px] h-[17px]" />
              {t('ordBtnProof')}
            </button>
            <button
              type="button"
              onClick={() => navigate('/lich-su')}
              className="h-[42px] rounded-lg bg-[#E4E4E7] flex items-center justify-center gap-2 px-2 text-[14px] font-bold text-[#1D1D1F] whitespace-nowrap"
              title={t('orderHistory')}
            >
              <Settings className="w-[17px] h-[17px]" />
              {t('orderHistory')}
            </button>
          </div>

          {/* 派单平台勾选：勾中的平台才参与随机派单 */}
          <div className="mt-4 grid grid-cols-4 gap-2">
            {ORDER_PLATFORMS.map((p) => {
              const on = platforms.includes(p);
              return (
                <label key={p} className="pf-check">
                  <input type="checkbox" checked={on} onChange={() => togglePlatform(p)} />
                  <span className="pf-box">
                    <svg viewBox="0 0 44 44" xmlns="http://www.w3.org/2000/svg">
                      <path className="pf-box-path" d="M12,4 H32 A8,8 0 0 1 40,12 V32 A8,8 0 0 1 32,40 H12 A8,8 0 0 1 4,32 V12 A8,8 0 0 1 12,4 Z" />
                      <path className="pf-check-path" d="M14,23 L19,28 L30,15" />
                    </svg>
                  </span>
                  <span className={`mt-1 block text-center text-[10px] leading-none ${on ? 'font-semibold text-[#1D1D1F]' : 'text-[#B9B9BE]'}`}>
                    {getPlatformMeta(p).label}
                  </span>
                </label>
              );
            })}
          </div>

          <div className="relative mt-3">
            {featured && !blockedByLucky && (
              <span aria-hidden="true" className="pointer-events-none absolute -inset-[1.5px] overflow-hidden rounded-[13px]">
                <span
                  className="animate-cta-spin absolute left-1/2 top-1/2"
                  style={{
                    width: '220%',
                    aspectRatio: '1',
                    background: 'conic-gradient(from 0deg, transparent 0deg, rgba(64,120,255,0.9) 50deg, rgba(147,197,253,0.95) 85deg, transparent 150deg, transparent 360deg)',
                  }}
                />
              </span>
            )}
            <button
              type="button"
              onClick={handlePrimary}
              disabled={claiming || blockedByLucky || !featured}
              className={`cta-btn relative flex h-[48px] w-full items-center justify-center gap-2 overflow-hidden rounded-xl bg-gradient-to-r from-[#4078FF] to-[#3E8FEF] text-[15px] font-bold text-white transition-transform duration-150 active:scale-[0.97] disabled:opacity-45 ${
                claiming ? 'cta-claiming' : ''
              }`}
            >
              <span aria-hidden="true" className="animate-cta-sheen pointer-events-none absolute inset-y-0 left-0 w-1/4 bg-gradient-to-r from-transparent via-white/25 to-transparent" />
              {claiming ? (
                <span className="flex items-center gap-2.5">
                  <span className="gpt-shimmer-text text-[15px] font-bold">{t('ordQueueing')}</span>
                  <span className="flex items-center gap-[3px]">
                    {[0, 1, 2].map((i) => (
                      <span key={i} className="queue-dot h-1 w-1 rounded-full bg-white" style={{ animationDelay: `${i * 0.15}s` }} />
                    ))}
                  </span>
                </span>
              ) : (
                <>
                  <Plus className="cta-icon w-5 h-5" strokeWidth={2.4} />
                  <span>
                    {primaryLabel.split('').map((ch, i) => (
                      <span key={i} className="cta-letter" style={{ animationDelay: `${(i % 12) * 0.08}s` }}>
                        {ch === ' ' ? '\u00A0' : ch}
                      </span>
                    ))}
                  </span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* 底部随机待接订单卡集：横向滚动，右下角标出点赞 / 评论 / 关注等动作类别 */}
      {offerTasks.length > 0 && (
        <div className="max-w-md mx-auto px-4 mt-6">
          <div className="overflow-x-auto scrollbar-hide snap-x snap-mandatory">
            <div className="flex gap-2.5 pb-1">
              {offerTasks.map((task, index) => {
                const { Icon: ActionIcon, verbKey } = getActionMeta(task.actionType);
                return (
                  <button
                    key={task.id}
                    type="button"
                    onClick={() => setActiveTask(task)}
                    aria-label={`${task.title} · ${t(verbKey)}`}
                    className="relative block shrink-0 w-[140px] h-[156px] rounded-xl overflow-hidden text-left snap-start"
                    style={{ backgroundColor: OFFER_COLORS[index % OFFER_COLORS.length] }}
                  >
                    <img
                      src={API_CONFIG.getImageUrl(task.imageUrl) || '/images/placeholder.svg'}
                      alt={task.title}
                      className="absolute inset-x-0 bottom-0 h-[74%] w-full object-cover"
                      onError={(e) => { e.target.src = '/images/placeholder.svg'; }}
                    />
                    <span className="absolute left-2.5 top-2.5 z-10 max-w-[100px] truncate rounded bg-white/25 px-2 py-0.5 text-[10px] font-semibold text-white backdrop-blur-sm">
                      {task.category || t('ordFeatured')}
                    </span>
                    <span
                      className="absolute bottom-2 right-2 z-10 flex w-8 h-8 items-center justify-center rounded-md bg-white/90 text-[#1D1D1F]"
                      title={t(verbKey)}
                    >
                      <ActionIcon className="w-4 h-4" strokeWidth={2.2} />
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <LuckyPayModal
        isOpen={luckyPayId != null}
        orderId={luckyPayId}
        onClose={() => setLuckyPayId(null)}
        onPaid={handleLuckyPaid}
      />

      {activeTask && (
        <TaskDetailSheet
          task={activeTask}
          onClose={() => setActiveTask(null)}
          onChanged={async () => { await load(); setActiveTask(null); }}
          onError={(message) => setToast({ type: 'error', message })}
        />
      )}
    </div>
  );
};

export default OrdersPage;
